import { useState, useEffect, useCallback, useRef } from "react";
import {
  adminMail,
  type MailDomain, type MailAccount, type MailAlias, type DnsRecords,
  type BatchMailboxResult,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Mail, Plus, Trash2, Key, Server, ArrowRight, RefreshCw,
  Globe, Copy, Check, Upload, List, Download, X, AlertCircle, CheckCircle2,
} from "lucide-react";
import clsx from "clsx";

// ── Helpers ───────────────────────────────────────────────────

function downloadCsv(rows: BatchMailboxResult[], filename: string) {
  const header = "email,success,message\n";
  const body = rows.map((r) => `${r.email},${r.success},"${r.message.replace(/"/g, '""')}"`).join("\n");
  const blob = new Blob([header + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Batch Panel ───────────────────────────────────────────────

interface BatchPanelProps {
  onClose: () => void;
  onDone: () => void;
}

type BatchMode = "paste" | "csv";

interface BatchSummary {
  total: number; created: number; failed: number; skipped: number;
}

function BatchPanel({ onClose, onDone }: BatchPanelProps) {
  const [mode, setMode]         = useState<BatchMode>("paste");
  const [pasteText, setPasteText] = useState("");
  const [csvFile, setCsvFile]   = useState<File | null>(null);
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState<BatchSummary | null>(null);
  const [results, setResults]   = useState<BatchMailboxResult[]>([]);
  const [error, setError]       = useState<string | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const failedRows  = results.filter((r) => !r.success && !r.alreadyExisted);
  const skippedRows = results.filter((r) => r.alreadyExisted);
  const createdRows = results.filter((r) => r.success);

  // Parse paste text to preview row count
  const pasteLines = pasteText
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && l.includes(",") && l.includes("@"));

  async function runBatch() {
    setError(null);
    setParseErrors([]);
    setLoading(true);
    setSummary(null);
    setResults([]);

    try {
      let resp;
      if (mode === "paste") {
        // Parse paste: email,password[,display_name]
        const accounts = pasteLines.map((line) => {
          const cols = line.split(",").map((c) => c.trim());
          return { email: cols[0], password: cols[1], display_name: cols[2] || undefined };
        });
        resp = await adminMail.createBatch(accounts);
      } else {
        if (!csvFile) { setError("No file selected"); setLoading(false); return; }
        const csvText = await csvFile.text();
        resp = await adminMail.createBatchCsv(csvText);
      }

      setSummary(resp.summary);
      setResults(resp.results);
      if (resp.parseErrors) setParseErrors(resp.parseErrors);
      if (resp.summary.created > 0) onDone(); // refresh account list
    } catch (err: any) {
      setError(err.message || "Batch creation failed");
    } finally {
      setLoading(false);
    }
  }

  const ready = mode === "paste" ? pasteLines.length > 0 : !!csvFile;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
          <div>
            <h2 className="font-bold text-lg">Batch Create Mailboxes</h2>
            <p className="text-xs text-slate mt-0.5">Create up to 100 mailboxes at once</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate hover:bg-black/[0.04]">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Mode toggle */}
          {!summary && (
            <div className="flex gap-2">
              {[
                { key: "paste" as BatchMode, label: "Paste Lines", icon: List },
                { key: "csv"   as BatchMode, label: "Upload CSV",  icon: Upload },
              ].map((m) => (
                <button key={m.key} onClick={() => setMode(m.key)}
                  className={clsx(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors",
                    mode === m.key
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-slate border-black/[0.08] hover:border-black/20"
                  )}>
                  <m.icon size={14} /> {m.label}
                </button>
              ))}
            </div>
          )}

          {/* Input area — only shown before submission */}
          {!summary && (
            <>
              {mode === "paste" ? (
                <div>
                  <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-2">
                    One account per line: <code className="font-mono lowercase normal-case bg-paper px-1 rounded">email,password,display_name</code>
                    <span className="text-slate font-normal ml-2">(display_name optional)</span>
                  </label>
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={10}
                    placeholder={
                      "alice@example.com,Password123\n" +
                      "bob@jobseven.com,Secure456,Bob Smith\n" +
                      "carol@dealzip.com,Welcome789"
                    }
                    spellCheck={false}
                    className="w-full px-4 py-3 rounded-xl border border-black/[0.08] text-sm font-mono bg-paper resize-none focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20"
                  />
                  {pasteLines.length > 0 && (
                    <p className="text-xs text-slate mt-1.5">
                      {pasteLines.length} account{pasteLines.length !== 1 ? "s" : ""} detected
                      {pasteLines.length > 100 && <span className="text-red-500 font-semibold ml-1">— max 100 per batch</span>}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-2">
                    CSV file — columns: <code className="font-mono lowercase normal-case bg-paper px-1 rounded">email,password,display_name</code>
                    <span className="text-slate font-normal ml-2">(header row optional)</span>
                  </label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    className={clsx(
                      "flex flex-col items-center justify-center gap-3 px-6 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-colors",
                      csvFile ? "border-coral/40 bg-coral/5" : "border-black/[0.12] bg-paper hover:border-black/25"
                    )}>
                    <Upload size={24} className={csvFile ? "text-coral" : "text-slate"} />
                    {csvFile ? (
                      <div className="text-center">
                        <p className="text-sm font-semibold text-ink">{csvFile.name}</p>
                        <p className="text-xs text-slate mt-0.5">{(csvFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-sm font-semibold">Click to upload CSV</p>
                        <p className="text-xs text-slate mt-0.5">or drag and drop</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    className="hidden"
                    onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  />
                  {csvFile && (
                    <button onClick={() => setCsvFile(null)}
                      className="mt-2 text-xs text-slate hover:text-red-500 flex items-center gap-1">
                      <X size={12} /> Remove file
                    </button>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 px-4 py-3 bg-red-50 rounded-xl text-sm text-red-700">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}

          {/* Results */}
          {summary && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: "Total",   value: summary.total,   color: "text-ink",         bg: "bg-paper" },
                  { label: "Created", value: summary.created, color: "text-teal",         bg: "bg-teal-light/40" },
                  { label: "Failed",  value: summary.failed,  color: "text-red-600",      bg: "bg-red-50" },
                  { label: "Skipped", value: summary.skipped, color: "text-amber-600",    bg: "bg-amber-50" },
                ].map((s) => (
                  <div key={s.label} className={clsx("rounded-xl px-4 py-3 text-center", s.bg)}>
                    <div className={clsx("text-2xl font-bold", s.color)}>{s.value}</div>
                    <div className="text-xs text-slate mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Parse errors */}
              {parseErrors.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-amber-700 mb-2">CSV parse warnings ({parseErrors.length})</p>
                  <ul className="space-y-0.5">
                    {parseErrors.map((e, i) => <li key={i} className="text-xs text-amber-700">{e}</li>)}
                  </ul>
                </div>
              )}

              {/* Per-account results table */}
              <div className="border border-black/[0.06] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-black/[0.02] border-b border-black/[0.06]">
                      <th className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Email</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate w-24">Status</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.email} className="border-b border-black/[0.04] last:border-0">
                        <td className="px-4 py-2.5 font-mono text-xs">{r.email}</td>
                        <td className="px-4 py-2.5">
                          {r.success ? (
                            <span className="flex items-center gap-1 text-teal text-[11px] font-semibold">
                              <CheckCircle2 size={13} /> Created
                            </span>
                          ) : r.alreadyExisted ? (
                            <span className="flex items-center gap-1 text-amber-600 text-[11px] font-semibold">
                              <AlertCircle size={13} /> Skipped
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 text-[11px] font-semibold">
                              <X size={13} /> Failed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Download failed rows */}
              {failedRows.length > 0 && (
                <button
                  onClick={() => downloadCsv(failedRows, "failed-mailboxes.csv")}
                  className="flex items-center gap-2 text-sm font-semibold text-slate hover:text-ink">
                  <Download size={14} /> Download failed rows as CSV to retry
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-black/[0.06]">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate hover:bg-black/[0.04]">
            {summary ? "Close" : "Cancel"}
          </button>

          {!summary && (
            <button
              onClick={runBatch}
              disabled={loading || !ready || (mode === "paste" && pasteLines.length > 100)}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50 transition-colors shadow-md shadow-coral/20">
              {loading ? (
                <><Spinner className="w-4 h-4" /> Creating…</>
              ) : (
                <>
                  <Mail size={15} />
                  Create {mode === "paste" && pasteLines.length > 0 ? `${Math.min(pasteLines.length, 100)} ` : ""}Mailboxes
                </>
              )}
            </button>
          )}

          {summary && summary.failed > 0 && (
            <button
              onClick={() => { setSummary(null); setResults([]); setParseErrors([]); setError(null); }}
              className="px-5 py-2 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark">
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function AdminMailPage() {
  const [tab, setTab] = useState<"domains" | "accounts" | "aliases" | "status">("domains");
  const [domains, setDomains]   = useState<MailDomain[]>([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [aliases, setAliases]   = useState<MailAlias[]>([]);
  const [services, setServices] = useState<Array<{ name: string; running: boolean }>>([]);
  const [loading, setLoading]   = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Single create form
  const [showCreate, setShowCreate]       = useState(false);
  const [newEmail, setNewEmail]           = useState("");
  const [newPassword, setNewPassword]     = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");

  // Batch create panel
  const [showBatch, setShowBatch] = useState(false);

  // Alias form
  const [showAliasCreate, setShowAliasCreate] = useState(false);
  const [aliasSource, setAliasSource]         = useState("");
  const [aliasDestination, setAliasDestination] = useState("");

  // Password change
  const [changingPw, setChangingPw] = useState<string | null>(null);
  const [newPw, setNewPw]           = useState("");

  // Domain management
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain]         = useState("");
  const [dnsView, setDnsView]             = useState<DnsRecords | null>(null);
  const [copiedField, setCopiedField]     = useState<string | null>(null);

  const loadDomains = useCallback(async () => {
    try {
      const res = await adminMail.domains();
      setDomains(res.domains);
    } catch (e) { console.error(e); }
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminMail.accounts(selectedDomain || undefined);
      setAccounts(res.accounts.filter((a) => a.status !== "deleted"));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedDomain]);

  const loadAliases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminMail.aliases();
      setAliases(res.aliases);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminMail.status();
      setServices(res.services);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDomains(); }, [loadDomains]);
  useEffect(() => {
    if (tab === "domains")  { loadDomains(); setLoading(false); }
    else if (tab === "accounts") loadAccounts();
    else if (tab === "aliases")  loadAliases();
    else if (tab === "status")   loadStatus();
  }, [tab, loadDomains, loadAccounts, loadAliases, loadStatus]);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setActionLoading("add-domain");
    try {
      const res = await adminMail.addDomain(newDomain.trim().toLowerCase());
      setShowAddDomain(false); setNewDomain("");
      setDnsView(res.dns);
      loadDomains();
    } catch (e: any) { alert(e.message || "Failed to add domain"); }
    finally { setActionLoading(null); }
  };

  const handleRemoveDomain = async (id: string, domain: string) => {
    if (!confirm(`Remove domain ${domain}? ALL mailboxes and mail for this domain will be permanently deleted.`)) return;
    setActionLoading(id);
    try { await adminMail.removeDomain(id); loadDomains(); }
    catch (e: any) { alert(e.message || "Failed"); }
    finally { setActionLoading(null); }
  };

  const handleViewDns = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await adminMail.domainDns(id);
      setDnsView(res.dns);
    } catch (e: any) { alert(e.message || "Failed to fetch DNS records"); }
    finally { setActionLoading(null); }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const handleCreate = async () => {
    if (!newEmail || !newPassword) return;
    setActionLoading("create");
    try {
      await adminMail.createAccount({ email: newEmail, password: newPassword, display_name: newDisplayName || undefined });
      setShowCreate(false); setNewEmail(""); setNewPassword(""); setNewDisplayName("");
      loadAccounts();
    } catch (e: any) { alert(e.message || "Failed"); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async (email: string) => {
    if (!confirm(`Delete mailbox ${email}? All mail will be permanently removed.`)) return;
    setActionLoading(email);
    try { await adminMail.deleteAccount(email); loadAccounts(); }
    catch (e: any) { alert(e.message || "Failed"); }
    finally { setActionLoading(null); }
  };

  const handleChangePassword = async (email: string) => {
    if (!newPw || newPw.length < 8) { alert("Password must be at least 8 characters"); return; }
    setActionLoading(email);
    try {
      await adminMail.changeMailboxPassword(email, newPw);
      setChangingPw(null); setNewPw("");
    } catch (e: any) { alert(e.message || "Failed"); }
    finally { setActionLoading(null); }
  };

  const handleCreateAlias = async () => {
    if (!aliasSource || !aliasDestination) return;
    setActionLoading("alias");
    try {
      await adminMail.createAlias(aliasSource, aliasDestination);
      setShowAliasCreate(false); setAliasSource(""); setAliasDestination("");
      loadAliases();
    } catch (e: any) { alert(e.message || "Failed"); }
    finally { setActionLoading(null); }
  };

  const handleDeleteAlias = async (id: string) => {
    setActionLoading(id);
    try { await adminMail.deleteAlias(id); loadAliases(); }
    catch (e: any) { alert(e.message || "Failed"); }
    finally { setActionLoading(null); }
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Mail Server</h1>
          <p className="text-sm text-slate">{domains.length} domains · {accounts.length} mailboxes</p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "domains" && (
            <button onClick={() => setShowAddDomain(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
              <Plus size={16} /> Add Domain
            </button>
          )}
          {tab === "accounts" && (
            <>
              <button onClick={() => setShowBatch(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] bg-white text-sm font-semibold hover:border-black/20 text-ink">
                <Upload size={15} /> Batch Create
              </button>
              <button onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
                <Plus size={16} /> New Mailbox
              </button>
            </>
          )}
          {tab === "aliases" && (
            <button onClick={() => setShowAliasCreate(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20">
              <Plus size={16} /> New Alias
            </button>
          )}
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Tabs */}
        <div className="flex gap-2">
          {[
            { key: "domains"  as const, label: "Domains",       icon: Globe },
            { key: "accounts" as const, label: "Mailboxes",     icon: Mail },
            { key: "aliases"  as const, label: "Aliases",       icon: ArrowRight },
            { key: "status"   as const, label: "Server Status", icon: Server },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={clsx("inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors",
                tab === t.key ? "bg-gray-900 text-white" : "bg-white text-slate border border-black/[0.06] hover:border-black/20"
              )}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {/* ─── Domains Tab ─── */}
        {tab === "domains" && (
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black/[0.02]">
                  {["Domain", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate border-b border-black/[0.06]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {domains.length === 0 ? (
                  <tr><td colSpan={3} className="px-6 py-12 text-center text-slate">No domains configured</td></tr>
                ) : domains.map((d) => (
                  <tr key={d.id} className="border-b border-black/[0.04] hover:bg-black/[0.01]">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Globe size={16} className="text-slate" />
                        <span className="font-semibold">{d.domain}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx("px-2 py-0.5 rounded-full text-[11px] font-semibold",
                        d.is_active ? "bg-teal-light text-teal" : "bg-red-50 text-red-500"
                      )}>{d.is_active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        <button onClick={() => handleViewDns(d.id)} disabled={actionLoading === d.id}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-black/[0.08] hover:border-black/20">
                          DNS Records
                        </button>
                        <button onClick={() => handleRemoveDomain(d.id, d.domain)} disabled={actionLoading === d.id}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ─── Accounts Tab ─── */}
        {tab === "accounts" && (
          <>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setSelectedDomain("")}
                className={clsx("px-3 py-1.5 rounded-full text-xs font-semibold",
                  !selectedDomain ? "bg-gray-900 text-white" : "bg-white text-slate border border-black/[0.06]"
                )}>All domains</button>
              {domains.filter((d) => d.is_active).map((d) => (
                <button key={d.domain} onClick={() => setSelectedDomain(d.domain)}
                  className={clsx("px-3 py-1.5 rounded-full text-xs font-semibold",
                    selectedDomain === d.domain ? "bg-gray-900 text-white" : "bg-white text-slate border border-black/[0.06]"
                  )}>{d.domain}</button>
              ))}
            </div>

            {loading ? <Spinner className="py-20" /> : (
              <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-black/[0.02]">
                      {["Email", "Domain", "Display Name", "Status", "Created", "Actions"].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate border-b border-black/[0.06]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-16 text-center">
                          <Mail size={32} className="mx-auto text-slate/30 mb-3" />
                          <p className="text-slate">No mailboxes found</p>
                          <p className="text-xs text-slate/60 mt-1">Use "New Mailbox" or "Batch Create" to add accounts</p>
                        </td>
                      </tr>
                    ) : accounts.map((a) => (
                      <tr key={a.id} className="border-b border-black/[0.04] hover:bg-black/[0.01]">
                        <td className="px-6 py-3 font-semibold">{a.email}</td>
                        <td className="px-6 py-3 text-slate">{a.domain}</td>
                        <td className="px-6 py-3 text-slate">{a.display_name || "—"}</td>
                        <td className="px-6 py-3">
                          <span className={clsx("px-2 py-0.5 rounded-full text-[11px] font-semibold",
                            a.status === "active" ? "bg-teal-light text-teal" : "bg-red-50 text-red-500"
                          )}>{a.status}</span>
                        </td>
                        <td className="px-6 py-3 text-xs text-slate">{new Date(a.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-3">
                          <div className="flex gap-1">
                            <button onClick={() => { setChangingPw(a.email); setNewPw(""); }}
                              disabled={actionLoading === a.email}
                              className="p-1.5 rounded-lg text-slate hover:bg-black/[0.04]" title="Change password">
                              <Key size={14} />
                            </button>
                            <button onClick={() => handleDelete(a.email)}
                              disabled={actionLoading === a.email}
                              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50" title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ─── Aliases Tab ─── */}
        {tab === "aliases" && (
          loading ? <Spinner className="py-20" /> : (
            <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-black/[0.02]">
                    {["From", "", "To", "Domain", "Actions"].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate border-b border-black/[0.06]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aliases.length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-12 text-center text-slate">No aliases configured</td></tr>
                  ) : aliases.map((a) => (
                    <tr key={a.id} className="border-b border-black/[0.04]">
                      <td className="px-6 py-3 font-semibold">{a.source_email}</td>
                      <td className="px-6 py-3 text-slate"><ArrowRight size={14} /></td>
                      <td className="px-6 py-3 font-semibold">{a.destination_email}</td>
                      <td className="px-6 py-3 text-slate">{a.domain}</td>
                      <td className="px-6 py-3">
                        <button onClick={() => handleDeleteAlias(a.id)} disabled={actionLoading === a.id}
                          className="p-1.5 rounded-lg text-red-400 hover:bg-red-50"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* ─── Status Tab ─── */}
        {tab === "status" && (
          loading ? <Spinner className="py-20" /> : (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold">Services</h2>
                  <button onClick={loadStatus} className="p-2 rounded-lg text-slate hover:bg-black/[0.04]"><RefreshCw size={16} /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {services.map((s) => (
                    <div key={s.name} className={clsx("flex items-center gap-3 px-4 py-3 rounded-xl border",
                      s.running ? "border-teal/20 bg-teal-light/30" : "border-red-200 bg-red-50/30"
                    )}>
                      <div className={clsx("w-2.5 h-2.5 rounded-full", s.running ? "bg-teal" : "bg-red-500")} />
                      <div>
                        <div className="text-sm font-semibold capitalize">{s.name}</div>
                        <div className="text-[11px] text-slate">{s.running ? "Running" : "Stopped"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
                <h2 className="font-bold mb-2">Hosted Domains</h2>
                <div className="flex flex-wrap gap-2">
                  {domains.map((d) => (
                    <span key={d.domain} className="px-3 py-1.5 bg-paper rounded-lg text-sm font-semibold">{d.domain}</span>
                  ))}
                </div>
              </div>
            </div>
          )
        )}
      </div>

      {/* ── Batch Create Panel ── */}
      {showBatch && (
        <BatchPanel
          onClose={() => setShowBatch(false)}
          onDone={() => { loadAccounts(); }}
        />
      )}

      {/* ── Single Create Mailbox Modal ── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-bold text-lg mb-4">Create Mailbox</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1">Email Address</label>
                <div className="flex gap-2">
                  <input value={newEmail.split("@")[0] || ""} onChange={(e) => setNewEmail(e.target.value + "@" + (newEmail.split("@")[1] || domains[0]?.domain || ""))}
                    className="flex-1 px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm" placeholder="username" />
                  <select value={newEmail.split("@")[1] || ""} onChange={(e) => setNewEmail((newEmail.split("@")[0] || "") + "@" + e.target.value)}
                    className="px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm">
                    {domains.filter((d) => d.is_active).map((d) => <option key={d.domain} value={d.domain}>@{d.domain}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1">Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm" placeholder="Min 8 characters" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1">Display Name (optional)</label>
                <input value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm" placeholder="John Doe" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate">Cancel</button>
              <button onClick={handleCreate} disabled={actionLoading === "create" || !newEmail || newPassword.length < 8}
                className="px-5 py-2 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50">
                {actionLoading === "create" ? "Creating..." : "Create Mailbox"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Alias Modal ── */}
      {showAliasCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAliasCreate(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-bold text-lg mb-4">Create Alias</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1">From (alias address)</label>
                <input value={aliasSource} onChange={(e) => setAliasSource(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm" placeholder="info@yourdomain.com" />
              </div>
              <div className="flex justify-center"><ArrowRight size={18} className="text-slate" /></div>
              <div>
                <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1">To (delivers to)</label>
                <input value={aliasDestination} onChange={(e) => setAliasDestination(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm" placeholder="admin@yourdomain.com" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAliasCreate(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate">Cancel</button>
              <button onClick={handleCreateAlias} disabled={actionLoading === "alias" || !aliasSource || !aliasDestination}
                className="px-5 py-2 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50">
                {actionLoading === "alias" ? "Creating..." : "Create Alias"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {changingPw && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setChangingPw(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-bold text-lg mb-1">Change Password</h2>
            <p className="text-sm text-slate mb-4">{changingPw}</p>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm mb-4" placeholder="New password (min 8 chars)" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setChangingPw(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate">Cancel</button>
              <button onClick={() => handleChangePassword(changingPw)} disabled={newPw.length < 8}
                className="px-5 py-2 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50">
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Domain Modal ── */}
      {showAddDomain && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddDomain(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-bold text-lg mb-4">Add Domain</h2>
            <p className="text-sm text-slate mb-4">This will configure Postfix, generate DKIM keys, and create the mail directory on the server.</p>
            <div>
              <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1">Domain Name</label>
              <input value={newDomain} onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                className="w-full px-3 py-2.5 rounded-lg border border-black/[0.08] text-sm" placeholder="example.com" />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddDomain(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate">Cancel</button>
              <button onClick={handleAddDomain} disabled={actionLoading === "add-domain" || !newDomain.trim()}
                className="px-5 py-2 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50">
                {actionLoading === "add-domain" ? "Adding..." : "Add Domain"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DNS Records Modal ── */}
      {dnsView && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDnsView(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-bold text-lg">DNS Records</h2>
                <p className="text-sm text-slate">{dnsView.domain} — add these to your DNS provider</p>
              </div>
              <button onClick={() => setDnsView(null)} className="text-slate hover:text-ink text-sm font-semibold">Close</button>
            </div>
            <div className="space-y-4">
              {[
                { label: "A Record",    type: "A",   name: `mail.${dnsView.domain}`,          value: dnsView.serverIp,                desc: "Points mail subdomain to server" },
                { label: "MX Record",   type: "MX",  name: dnsView.domain,                    value: `10 mail.${dnsView.domain}`,     desc: "Routes incoming mail to this server" },
                { label: "SPF Record",  type: "TXT", name: dnsView.domain,                    value: dnsView.spf,                     desc: "Authorizes server to send for this domain" },
                { label: "DMARC Record",type: "TXT", name: `_dmarc.${dnsView.domain}`,        value: dnsView.dmarc,                   desc: "Email authentication policy" },
                { label: "DKIM Record", type: "TXT", name: `mail._domainkey.${dnsView.domain}`,value: dnsView.dkim || "(not yet generated)", desc: "Email signing key" },
              ].map((rec) => (
                <div key={rec.label} className="bg-paper rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-slate">{rec.label}</span>
                      <span className="text-[10px] text-slate ml-2 bg-white px-1.5 py-0.5 rounded font-mono">{rec.type}</span>
                    </div>
                    <button onClick={() => copyToClipboard(rec.value, rec.label)} className="p-1.5 rounded-lg text-slate hover:bg-white">
                      {copiedField === rec.label ? <Check size={14} className="text-teal" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <div className="text-xs text-slate mb-1">Name: <span className="font-mono font-semibold text-ink">{rec.name}</span></div>
                  <div className="text-xs font-mono bg-white rounded-lg p-2.5 break-all text-ink border border-black/[0.06]">{rec.value}</div>
                  <div className="text-[11px] text-slate mt-1.5">{rec.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
